import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { memo, useCallback } from 'react';
import * as yup from 'yup';
import FormArea from './area';
import FormStreet from './street';
import CadastralAccordion from './cadastral-data';
import { CadastralVM } from 'shell/view-models/cadastral';
import { ItemVM } from 'shell/view-models/item';
import { MunicipalMessengerMessengerListItem } from 'shell/models/municipal-messenger-messenger-list';
import { AbilityChangesType } from 'system/pages/toponymy/circulation-area/edit';
import { KeyCirculationArea } from 'shell/models/circulation-area';

export type CivicItemVM = ItemVM & { esponente?: string; dataIstituzione?: string };

export interface CirculationAreaForm {
  code: string;
  type: string;
  streetNumber?: CivicItemVM;
  terminate: boolean;
  deliberated: boolean;
  name: string;
  descriptionReducedPostal?: string;
  descriptionReduced?: string;
  estabilishmentDate: DateTime | null;
  endDate: DateTime | null;
  officeNumber: number;
  electoralSection: string;
  civic: string;
  endReason: string;
  civicState: string;
  municipality: string;
  entered: boolean;
  estabilishmentReason: string;
  temporary: boolean;
  streetEnd: boolean;
  cap: string;
  typeDeliberated?: string;
  keyFromData?: KeyCirculationArea;
  key?: string;
  dateKey?: DateTime | null;
  estabilishmentDateCivic?: DateTime | null;
  endDateCivic?: DateTime | null;
  urbanArea?: string;
  civicType?: string;
  addressSection?: string;
  territorialArea?: string;
  municipalMessenger?: ItemVM<MunicipalMessengerMessengerListItem> | null;
  censusSection?: string;
  localIdentity?: string;
  cadastralList?: Array<CadastralVM>;
}

const defaultValidationSchema = yup.object({});

interface FormCirculationAreaProps {
  onOk: (form?: CirculationAreaForm) => void;
  onCancel: (form?: CirculationAreaForm) => void;
  title: string;
  disabled?: boolean;
  initialValues?: CirculationAreaForm;
  circulationArea?: number;
  newArea?: boolean;
  abilityChanges?: AbilityChangesType;
  setAbilityChanges?: (newState: AbilityChangesType) => void;
  onSaveCirculationArea?: (values: CirculationAreaForm) => void;
  validationSchema?: yup.AnySchema;
}

const FormCirculationAreaComponent = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
  circulationArea,
  newArea,
  abilityChanges,
  setAbilityChanges,
  onSaveCirculationArea,
  validationSchema = defaultValidationSchema,
}: FormCirculationAreaProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const circulationAreaForm = useFormik<CirculationAreaForm>({
    initialValues: initialValues || {
      code: '',
      type: '',
      typeDeliberated: '',
      terminate: false,
      deliberated: false,
      name: '',
      estabilishmentDate: DateTime.now(),
      endDate: DateTime.now(),
      officeNumber: 0,
      electoralSection: '',
      civic: '',
      endReason: '',
      civicState: '',
      municipality: '',
      entered: false,
      estabilishmentReason: '',
      temporary: false,
      streetEnd: false,
      cap: '',
      key: undefined,
      urbanArea: '',
      civicType: '',
      addressSection: '',
      territorialArea: '',
      municipalMessenger: null,
      censusSection: '',
      localIdentity: '',
      cadastralList: [],
      streetNumber: undefined,
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <div className="pmi-my-3">
      <form onSubmit={circulationAreaForm.handleSubmit}>
        <div style={{ padding: '24px' }}>
          <FormArea
            title={title}
            circulationAreaForm={circulationAreaForm.values || initialValues}
            disabled={!newArea}
            handleChange={circulationAreaForm.setFieldValue}
            handleChanges={circulationAreaForm.setValues}
            newArea={newArea || false}
            errors={circulationAreaForm.errors}
            touched={circulationAreaForm.touched}
            abilityChanges={abilityChanges}
            setAbilityChanges={setAbilityChanges}
            onSaveCirculationArea={onSaveCirculationArea}
          />
          {!newArea && (
            <FormStreet
              title="Civico"
              circulationAreaForm={circulationAreaForm}
              circulationArea={circulationArea}
              disabled={disabled}
            />
          )}
          {circulationAreaForm.values.streetNumber?.id &&
          circulationAreaForm.values?.cadastralList?.length &&
          circulationAreaForm.values?.cadastralList?.length > 0 ? (
            <CadastralAccordion
              cadastralList={circulationAreaForm.values?.cadastralList || []}
              handleChange={circulationAreaForm.setFieldValue}
              isEdit={!disabled}
            />
          ) : undefined}
        </div>
        <div style={{ padding: '24px 24px 16px 24px' }}>
          <div className="pmi-mr-3 pmi-d-inline-block">
            <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
              Conferma
            </PmiButton>
          </div>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(circulationAreaForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </div>
      </form>
    </div>
  );
};

FormCirculationAreaComponent.displayName = 'Form Circulation Area component';

const FormCirculationArea = memo(FormCirculationAreaComponent);

export default FormCirculationArea;
